<div class="modal-overlay" (click)="onBackdrop()">
  <div class="modal" (click)="$event.stopPropagation()">

    <!-- HEADER -->
    <div class="modal-header">
      <div class="modal-title-group">
        <span class="modal-title">{{ detail.name }}</span>
        <code class="modal-eui">{{ detail.deviceEui }}</code>
        <span class="device-type-badge" *ngIf="detail.deviceTypeCode">{{ deviceTypeLabel(detail.deviceTypeCode) }}</span>
        <span class="status-badge" [class.badge-active]="detail.active" [class.badge-inactive]="!detail.active">
          {{ (detail.active ? 'equipements.detailModal.active' : 'equipements.detailModal.inactive') | translate }}
        </span>
      </div>
      <button class="btn-close" (click)="close.emit()">×</button>
    </div>

    <!-- BODY -->
    <div class="modal-body">

      <!-- ── Identification ── -->
      <div class="section">
        <h4 class="section-title">{{ 'equipements.detailModal.identification' | translate }}</h4>
        <div class="info-grid">
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.location' | translate }}</span>
            <span class="info-value">{{ detail.location || '—' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.common.linkedQueue' | translate }}</span>
            <span class="info-value">
              <code *ngIf="detail.queueId; else noQueue" class="code-sm">{{ detail.queueId }}</code>
              <ng-template #noQueue><span class="text-muted">{{ 'equipements.detailModal.notConfigured' | translate }}</span></ng-template>
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.addedOn' | translate }}</span>
            <span class="info-value">{{ formatDate(detail.createdAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.updatedOn' | translate }}</span>
            <span class="info-value">{{ formatDate(detail.updatedAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.syncCursor' | translate }}</span>
            <span class="info-value"><code class="code-sm">{{ truncateCursor(detail.lastFetchCursor) }}</code></span>
          </div>
        </div>
      </div>

      <!-- ── Dernière trame ── -->
      <div class="section" *ngIf="detail.latestFrame as f; else noFrame">
        <h4 class="section-title">{{ 'equipements.detailModal.lastFrame' | translate }}</h4>

        <div class="info-grid">
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.receivedOn' | translate }}</span>
            <span class="info-value">{{ formatDate(f.receivedAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.fetchedOn' | translate }}</span>
            <span class="info-value">{{ formatDate(f.fetchedAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.orangeId' | translate }}</span>
            <span class="info-value"><code class="code-sm">{{ f.orangeMessageId || '—' }}</code></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.gateway' | translate }}</span>
            <span class="info-value"><code class="code-sm">{{ f.gatewayId || '—' }}</code></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'equipements.detailModal.frameCounter' | translate }}</span>
            <span class="info-value">{{ formatInt(f.frameCounter) }}</span>
          </div>
        </div>

        <div class="radio-row">
          <div class="radio-chip" [ngClass]="getRssiClass(f.rssi)">
            <span class="radio-label">RSSI</span>
            <span class="radio-value">{{ formatRssi(f.rssi) }}</span>
          </div>
          <div class="radio-chip">
            <span class="radio-label">SNR</span>
            <span class="radio-value">{{ formatSnr(f.snr) }}</span>
          </div>
          <div class="radio-chip">
            <span class="radio-label">SF</span>
            <span class="radio-value">{{ formatSf(f.spreadingFactor) }}</span>
          </div>
        </div>

        <div class="payload-block" *ngIf="f.payloadHex">
          <div class="payload-label">{{ 'equipements.detailModal.payloadHex' | translate }}</div>
          <code class="payload-hex">{{ f.payloadHex }}</code>
        </div>

        <div class="payload-block" *ngIf="f.payloadDecoded">
          <div class="payload-label">{{ 'equipements.detailModal.payloadDecoded' | translate }}</div>
          <div class="payload-decoded-grid">
            <div class="decoded-row" *ngFor="let entry of payloadEntries(f.payloadDecoded)">
              <span class="decoded-key">{{ entry.key }}</span>
              <span class="decoded-val">
                {{ entry.value }}
                <span class="decoded-hint" *ngIf="entry.hint">— {{ entry.hint }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <ng-template #noFrame>
        <div class="section">
          <h4 class="section-title">{{ 'equipements.detailModal.lastFrame' | translate }}</h4>
          <p class="text-muted">{{ 'equipements.detailModal.noFrame' | translate }}</p>
        </div>
      </ng-template>

      <!-- ── Historique 100 dernières trames ── -->
      <div class="section" *ngIf="detail.recentFrames.length > 0">
        <h4 class="section-title">{{ 'equipements.detailModal.historyTitle' | translate: { count: detail.recentFrames.length } }}</h4>
        <div class="history-table-wrap">
          <table class="history-table">
            <thead>
              <tr>
                <th>{{ 'equipements.detailModal.table.date' | translate }}</th>
                <th>RSSI</th>
                <th>SNR</th>
                <th>SF</th>
                <th>{{ 'equipements.detailModal.table.periodicIn' | translate }}</th>
                <th>{{ 'equipements.detailModal.table.periodicOut' | translate }}</th>
                <th>{{ 'equipements.detailModal.table.cumulativeIn' | translate }}</th>
                <th>{{ 'equipements.detailModal.table.cumulativeOut' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let f of detail.recentFrames">
                <td class="td-date">{{ formatShortDate(f.receivedAt) }}</td>
                <td [ngClass]="getRssiClass(f.rssi)">{{ formatRssi(f.rssi) }}</td>
                <td>{{ formatSnr(f.snr) }}</td>
                <td>{{ formatSf(f.spreadingFactor) }}</td>
                <td class="td-in">{{ formatInt(f.periodicIn) }}</td>
                <td class="td-out">{{ formatInt(f.periodicOut) }}</td>
                <td class="td-in">{{ formatInt(f.accumulatedIn) }}</td>
                <td class="td-out">{{ formatInt(f.accumulatedOut) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- FOOTER -->
    <div class="modal-footer">
      <button class="btn-close-footer" (click)="close.emit()">{{ 'equipements.common.close' | translate }}</button>
    </div>

  </div>
</div>
